import { z } from 'zod'

import { useTranslation } from '@flecto/translations'
import { type CoreUserBookingsSettings } from '@flecto/types'

type UseProfileBookingsSchemaOptions = {
  payload?: Partial<CoreUserBookingsSettings>
}

export function useProfileBookingsSchema(
  options?: UseProfileBookingsSchemaOptions
) {
  const { t } = useTranslation('shared', { keyPrefix: 'validation' })

  const schema = z
    .object({
      default_booking_end_at: z.string().optional(),
      default_booking_start_at: z.string().optional(),
      documents_quote_include_terms_and_conditions: z.boolean().optional(),
      documents_quote_validity_period: z.number().optional(),
      documents_quote_validity_period_active: z.boolean().optional(),
      documents_quote_include_links: z.boolean().optional(),
      documents_quote_include_signatures: z.boolean().optional(),
      contract_body: z.string().nullable().optional(),
      contract_title: z.string().nullable().optional(),
      documents_contract_include_customer_details: z.boolean().optional(),
      documents_contract_include_rental_details: z.boolean().optional(),
      documents_contract_include_signatures: z.boolean().optional(),
      documents_contract_include_links: z.boolean().optional(),
      documents_contract_include_terms_and_conditions: z.boolean().optional(),
      documents_contract_include_id_on_title: z.boolean().optional(),
      documents_contract_include_generated_date: z.boolean().optional(),
    })
    .refine(
      (data) => {
        if (data.documents_quote_validity_period_active) {
          return (
            data.documents_quote_validity_period !== undefined &&
            data.documents_quote_validity_period >= 1
          )
        }
        return true
      },
      {
        message: t('minimum', { quantity: 0 }),
        path: ['documents_quote_validity_period'],
      }
    )

  const initialValues: CoreUserBookingsSettings = {
    default_booking_end_at: options?.payload?.default_booking_end_at ?? '',
    default_booking_start_at: options?.payload?.default_booking_start_at ?? '',
    documents_quote_validity_period_active:
      options?.payload?.documents_quote_validity_period_active ?? true,
    documents_quote_validity_period: options?.payload
      ?.documents_quote_validity_period_active
      ? Number(options?.payload?.documents_quote_validity_period)
      : 30,
    documents_quote_include_signatures:
      options?.payload?.documents_quote_include_signatures ?? true,
    documents_quote_include_links:
      options?.payload?.documents_quote_include_links ?? true,
    documents_quote_include_terms_and_conditions:
      options?.payload?.documents_quote_include_terms_and_conditions ?? true,
    contract_body: options?.payload?.contract_body ?? '',
    contract_title: options?.payload?.contract_title ?? '',
    documents_contract_include_customer_details:
      options?.payload?.documents_contract_include_customer_details ?? true,
    documents_contract_include_rental_details:
      options?.payload?.documents_contract_include_rental_details ?? true,
    documents_contract_include_signatures:
      options?.payload?.documents_contract_include_signatures ?? true,
    documents_contract_include_links:
      options?.payload?.documents_contract_include_links ?? true,
    documents_contract_include_terms_and_conditions:
      options?.payload?.documents_contract_include_terms_and_conditions ?? true,
    documents_contract_include_id_on_title:
      options?.payload?.documents_contract_include_id_on_title ?? true,
    documents_contract_include_generated_date:
      options?.payload?.documents_contract_include_generated_date ?? true,
    documents_proforma_include_due_date:
      options?.payload?.documents_proforma_include_due_date ?? true,
    documents_proforma_due_date_in_days:
      options?.payload?.documents_proforma_due_date_in_days ?? 30,
    documents_proforma_include_payment_details:
      options?.payload?.documents_proforma_include_payment_details ?? true,
    documents_proforma_payment_details:
      options?.payload?.documents_proforma_payment_details ?? '',
    documents_proforma_include_links:
      options?.payload?.documents_proforma_include_links ?? true,
    documents_proforma_include_terms_and_conditions:
      options?.payload?.documents_proforma_include_terms_and_conditions ?? true,
  }

  return { schema, initialValues }
}
